import { useState } from "react";
import { Input } from "@/components/ui/input";
import { UNITS, ublLineAmounts, r2 } from "@/components/InvoiceItemsEditor";
import { TRY } from "@/lib/format";

const numCls = "h-8 text-right tnum text-[13px] px-2";
const round4 = (v) => Math.round(v * 10000) / 10000;

// KDV dahil birim fiyat: yazılırken metin korunur; KDV hariç birim fiyat (4 hane, GİB PriceAmount) ondan hesaplanır
function InclPriceInput({ value, onCommit, tid }) {
  const [txt, setTxt] = useState(null);
  return (
    <Input type="number" inputMode="decimal" step="any" min="0" value={txt ?? value} data-testid={tid}
      onFocus={() => setTxt(String(value))} onBlur={() => setTxt(null)}
      onChange={(e) => { setTxt(e.target.value); onCommit(Number(e.target.value) || 0); }}
      className={`${numCls} font-semibold border-indigo-300 bg-indigo-50/50 dark:bg-indigo-950/20`} />
  );
}
const TH = ({ children, sub, className = "" }) => (
  <th className={`px-2 py-2 align-bottom whitespace-nowrap ${className}`}>
    <span className="block leading-tight">{children}</span>
    {sub && <span className="block leading-tight text-[9px] font-semibold normal-case tracking-normal text-muted-foreground/80">{sub}</span>}
  </th>
);

// Fatura kalemleri — kesmeden önce tablo halinde düzenlenir (ürün adı, kod, miktar, birim, KDV DAHİL birim fiyat, iskonto).
// KDV dahil birim fiyat düzenlenir; KDV hariç karşılığı yanında hesaplanır; tutar da KDV hariç / dahil gösterilir.
export function InvoiceLinesTable({ lines, onChange, kdvRate }) {
  const set = (i, patch) => onChange(lines.map((l, j) => (j === i ? { ...l, ...patch } : l)));
  const k = 1 + (Number(kdvRate) || 0) / 100;
  return (
    <div className="overflow-x-auto rounded-xl border bg-card" data-testid="ci-lines">
      <table className="w-full min-w-[900px] table-fixed text-sm">
        <colgroup>
          <col className="w-8" /><col /><col className="w-[92px]" /><col className="w-[70px]" /><col className="w-[86px]" />
          <col className="w-[112px]" /><col className="w-[94px]" /><col className="w-[62px]" /><col className="w-[100px]" /><col className="w-[108px]" />
        </colgroup>
        <thead className="bg-muted/70 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
          <tr>
            <TH className="text-left">#</TH>
            <TH className="text-left">Ürün / Hizmet Adı</TH>
            <TH className="text-left">Kod</TH>
            <TH className="text-right">Miktar</TH>
            <TH className="text-left">Birim</TH>
            <TH className="text-right" sub={`KDV dahil (%${Number(kdvRate) || 0})`}>Birim Fiyat</TH>
            <TH className="text-right" sub="KDV hariç">Birim Fiyat</TH>
            <TH className="text-right">İsk. %</TH>
            <TH className="text-right" sub="KDV hariç">Tutar</TH>
            <TH className="text-right" sub="KDV dahil">Tutar</TH>
          </tr>
        </thead>
        <tbody className="divide-y">
          {lines.map((l, i) => {
            const a = ublLineAmounts(l, kdvRate, 0);
            const units = UNITS.includes(l.unit) ? UNITS : [l.unit, ...UNITS];
            const netUnit = (Number(l.unit_price) || 0) * (1 - (Number(l.discount_rate) || 0) / 100);
            const setIncl = (v) => { const d = 1 - (Number(l.discount_rate) || 0) / 100; set(i, { unit_price: d > 0 ? round4(v / k / d) : 0 }); };
            return (
              <tr key={l.key || i} className="hover:bg-muted/30 transition-colors" data-testid={`ci-line-${i}`}>
                <td className="px-2 text-xs text-muted-foreground tnum">{i + 1}</td>
                <td className="px-1.5 py-1.5"><textarea rows={2} value={l.name} onChange={(e) => set(i, { name: e.target.value.replace(/\n/g, " ") })} title={l.name} className={`block w-full resize-none rounded-md border bg-background px-2 py-1 text-[12.5px] leading-snug focus:outline-none focus:ring-2 focus:ring-ring ${String(l.name || "").trim() ? "border-input" : "border-red-400"}`} data-testid={`ci-line-name-${i}`} /></td>
                <td className="px-1.5"><Input value={l.code} onChange={(e) => set(i, { code: e.target.value })} className="h-8 text-[12px]" data-testid={`ci-line-code-${i}`} /></td>
                <td className="px-1.5"><Input type="number" inputMode="decimal" step="any" value={l.quantity} onChange={(e) => set(i, { quantity: e.target.value })} className={numCls} data-testid={`ci-line-qty-${i}`} /></td>
                <td className="px-1.5">
                  <select value={l.unit} onChange={(e) => set(i, { unit: e.target.value })} className="h-8 w-full rounded-md border border-input bg-background px-1.5 text-[12px]" data-testid={`ci-line-unit-${i}`}>
                    {units.map((u) => <option key={u} value={u}>{u}</option>)}
                  </select>
                </td>
                <td className="px-1.5"><InclPriceInput value={r2(netUnit * k)} onCommit={setIncl} tid={`ci-line-price-incl-${i}`} /></td>
                <td className="px-2 text-right tnum text-[12.5px] text-muted-foreground whitespace-nowrap" data-testid={`ci-line-price-${i}`}>{TRY(r2(netUnit))}</td>
                <td className="px-1.5"><Input type="number" inputMode="decimal" step="any" min="0" max="99.99" value={l.discount_rate} onChange={(e) => set(i, { discount_rate: e.target.value })} className={numCls} data-testid={`ci-line-disc-${i}`} /></td>
                <td className="px-2 text-right tnum text-[12.5px] whitespace-nowrap" data-testid={`ci-line-total-${i}`}>{TRY(a.net)}</td>
                <td className="px-2 text-right tnum font-bold whitespace-nowrap" data-testid={`ci-line-total-incl-${i}`}>{TRY(r2(a.net + a.tax))}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
